import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import {
  Bid,
  Account,
  Contact,
  Opportunity,
  Activity
} from "types/common/ServiceStoreTypes";
import { Job as Project } from "types/common/ServiceStoreTypes";

export class BidDetailStore {
  rootStore: RootStore;

  // Observables
  isLoading = false;
  error: string | null = null;
  activitiesError: string | null = null;

  // Constructor
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Data loading methods
  async loadBids() {
    this.isLoading = true;
    this.error = null;

    try {
      await this.bidsStore.find();
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to load bids";
      console.error("Failed to load bids:", error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadRelatedData() {
    try {
      // Load all base entities from ServiceStores to ensure relational data is available
      await Promise.all([
        // Load all base entities without queries to populate stores
        this.bidsStore.find(),
        this.opportunitiesStore.find(),
        this.customersStore.find(),
        this.contactsStore.find(),
      ]);
    } catch (error) {
      console.error("Failed to load related data:", error);
    }
  }

  // Actions
  async updateBid(updates: Partial<Bid>): Promise<boolean> {
    if (!this.dialogBid) return false;

    try {
      await this.bidsStore.patch(this.dialogBid.bidId, {
        ...updates,
        // UpdatedAt: new Date().toISOString(),
      });
      return true;
    } catch (error) {
      console.error("Failed to update bid:", error);
      runInAction(() => {
        this.error = "Failed to update bid. Please try again.";
      });
      return false;
    }
  }

  async deleteBid(): Promise<boolean> {
    if (!this.dialogBid) return false;

    try {
      await this.bidsStore.remove(this.dialogBid.bidId);
      return true;
    } catch (error) {
      console.error("Failed to delete bid:", error);
      runInAction(() => {
        this.error = "Failed to delete bid. Please try again.";
      });
      return false;
    }
  }

  async cloneBid(): Promise<Bid | null> {
    if (!this.dialogBid) return null;

    try {
      const clonedBid = await this.bidsStore.create({
        ...this.dialogBid,
        bidId: undefined, // Remove ID so a new one is generated
        BidNumber: `${this.dialogBid.bidNumber}-COPY`,
        Status: "Draft",
        SubmissionDate: null,
        CreatedAt: new Date().toISOString(),
        UpdatedAt: new Date().toISOString(),
      });
      return clonedBid as unknown as Bid;
    } catch (error) {
      console.error("Failed to clone bid:", error);
      runInAction(() => {
        this.error = "Failed to clone bid. Please try again.";
      });
      return null;
    }
  }

  // Computed properties
  get bidMetrics() {
    if (!this.dialogBid) return null;

    const daysToExpiry = this.dialogBid.validUntilDate
      ? Math.ceil(
          (new Date(this.dialogBid.validUntilDate || Date.now()).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24)
        )
      : null;

    const daysSinceSubmission = this.dialogBid.submissionDate
      ? Math.ceil(
          (Date.now() - new Date(this.dialogBid.submissionDate || Date.now()).getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : null;

    return {
      bidAmount: this.dialogBid.bidAmount || 0,
      daysToExpiry,
      daysSinceSubmission,
      isExpiringSoon: daysToExpiry !== null && daysToExpiry <= 7,
      isSubmitted:
        this.dialogBid.status === "Submitted" ||
        this.dialogBid.status === "Under Review",
    };
  }

  get canEdit(): boolean {
    return this.dialogBid?.status === "Draft";
  }

  get canSubmit(): boolean {
    return this.dialogBid?.status === "Draft";
  }

  get canCancel(): boolean {
    return (
      this.dialogBid?.status === "Draft" ||
      this.dialogBid?.status === "Submitted" ||
      this.dialogBid?.status === "Under Review"
    );
  }

  get dialogBid(): Bid | null {
    const bidsStore = this.rootStore.bidsStore as ServiceStore;
    const bidId = Number(this.rootStore.routerStore?.queryParams.dialogContext);

    if (!bidId || !bidsStore?.entities) {
      return null;
    }

    return bidsStore.entities.find((bid: unknown) => {
      const bidData = bid as Bid;
      return bidData.bidId === bidId;
    }) as unknown as Bid | null;
  }

  get opportunity(): Opportunity | null {
    if (!this.dialogBid?.opportunityId) return null;

    const opportunity = this.opportunitiesStore.entities.find(
      (opp: unknown) => {
        const oppData = opp as { opportunityId: number };
        return oppData.opportunityId === this.dialogBid!.opportunityId;
      }
    );

    return opportunity ? (opportunity as unknown as Opportunity) : null;
  }

  get account(): Account | null {
    if (!this.opportunity?.accountId) return null;

    const account = this.customersStore.entities.find((acc: unknown) => {
      const accountData = acc as { accountId: number };
      return accountData.accountId === this.opportunity!.accountId;
    });

    return account ? (account as unknown as Account) : null;
  }

  get contact(): Contact | null {
    if (!this.opportunity?.contactId) return null;

    const contact = this.contactsStore.entities.find((cont: unknown) => {
      const contactData = cont as { contactId: number };
      return contactData.contactId === this.opportunity!.contactId;
    });

    return contact ? (contact as unknown as Contact) : null;
  }

  get activities(): Activity[] {
    if (!this.dialogBid?.bidId) return [];

    return this.activitiesStore.entities.filter((activity: unknown) => {
      const activityData = activity as { bidId?: number };
      return activityData.bidId === this.dialogBid!.bidId;
    }) as unknown as Activity[];
  }

  get project(): Project | null {
    if (!this.opportunity?.opportunityId) return null;

    // A project could be created from the opportunity that this bid is for
    const project = this.jobStore.entities.find((proj: unknown) => {
      const projectData = proj as { OpportunityId?: number };
      return projectData.OpportunityId === this.opportunity!.opportunityId;
    });

    return project ? (project as unknown as Project) : null;
  }

  // Service store access
  get bidsStore(): ServiceStore {
    return this.rootStore.bidsStore as ServiceStore;
  }

  get opportunitiesStore(): ServiceStore {
    return this.rootStore.opportunitiesStore as ServiceStore;
  }

  get customersStore(): ServiceStore {
    return this.rootStore.customersStore as ServiceStore;
  }

  get contactsStore(): ServiceStore {
    return this.rootStore.contactsStore as ServiceStore;
  }

  get activitiesStore(): ServiceStore {
    return this.rootStore.activitiesStore as ServiceStore;
  }

  get jobStore(): ServiceStore {
    return this.rootStore.jobStore as ServiceStore;
  }

  get availableStatuses(): string[] {
    return [
      "Draft",
      "Submitted",
      "Under Review",
      "Awarded",
      "Lost",
      "Cancelled",
    ];
  }

  // Clear state
  clear() {
    runInAction(() => {
      this.error = null;
      this.activitiesError = null;
    });
  }
}

export default BidDetailStore;
